import { Hero } from "@/components/hero"
import { CareerSection } from "@/components/career-section"
import { HorizontalSlider } from "@/components/horizontal-slider"
import {
  PRODUCT_CAROUSEL_INTERVALS,
  ProductImageCarousel,
} from "@/components/product-image-carousel"
import { getDefaultMetaDescriptors } from "@/components/seo"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { getLocalizedValue, type Locale, useI18n } from "@/lib/i18n"
import { organizationJsonLd } from "@/lib/jsonld"
import {
  getProductImages,
  pageHomeFragment,
  productsListFragment,
  resourcesListFragment,
  sanityClient,
  urlForImage,
} from "@/lib/sanity"
import type {
  LocalizedString,
  PageHome,
  Product,
  Resource,
} from "@/types/sanity"
import {
  faArrowRight,
  faCalendar,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { motion } from "framer-motion"
import { type FC, type JSX, useEffect, useState } from "react"
import { Link } from "react-router"
import { type Route } from "../+types/root"

export const meta: Route.MetaFunction = ({ matches }) => {
  const layoutMatch = matches.find((match) => match?.id === "routes/layout") as
    | { data?: { lang: Locale } }
    | undefined

  const lang = layoutMatch?.data?.lang ?? "tr"
  return [
    {
      title:
        lang === "tr"
          ? "Poremat | Sürdürülebilir Gözenekli Malzeme Çözümleri"
          : "Poremat | Sustainable Porous Material Solutions",
    },
    // add json-ld meta descriptors
    {
      "script:ld+json": organizationJsonLd(),
    },

    ...getDefaultMetaDescriptors(lang),
  ]
}

// Helper functions
const getHeroTitle = (pageData: PageHome | null, locale: Locale): string => {
  if (pageData?.heroTitle) {
    return getLocalizedValue(pageData.heroTitle, locale, "")
  }
  return locale === "tr"
    ? "İleri Hidrojen Depolama Çözümleri"
    : "Advanced Hydrogen Storage Solutions"
}

const getHeroSubtitle = (pageData: PageHome | null, locale: Locale): string => {
  if (pageData?.heroSubtitle) {
    return getLocalizedValue(pageData.heroSubtitle, locale, "")
  }
  return locale === "tr"
    ? "MOF Bazlı Yenilikçi Malzemeler"
    : "Innovative MOF-Based Materials"
}

const getHeroCta = (pageData: PageHome | null, locale: Locale): string => {
  if (pageData?.heroCta) {
    return getLocalizedValue(pageData.heroCta, locale, "")
  }
  return locale === "tr" ? "Daha Fazla Bilgi" : "Learn More"
}

const renderFeatureIcon = (): JSX.Element => (
  <FontAwesomeIcon icon={faCheckCircle} className="w-6 h-6" />
)

const renderStatIcon = (): JSX.Element => (
  <FontAwesomeIcon icon={faCheckCircle} className="w-8 h-8" />
)

const scrollToFeatures = (): void => {
  const element = document.getElementById("features")
  if (element !== null) {
    element.scrollIntoView({ behavior: "smooth" })
  }
}

// Statistics Section Component
type StatisticsSectionProps = {
  statistics: Array<{ value: string; label: LocalizedString; icon: string }>
  locale: Locale
}

const StatisticsSection: FC<StatisticsSectionProps> = ({
  statistics,
  locale,
}) => (
  <section className="relative py-20 bg-linear-to-r from-[#0077B6] via-[#1FC1B1] to-[#568F22] text-white overflow-hidden">
    <div className="absolute inset-0 opacity-10">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle, white 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {statistics.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="text-center"
          >
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                {renderStatIcon()}
              </div>
            </div>
            <div className="text-4xl md:text-5xl font-bold mb-2">
              {stat.value}
            </div>
            <div className="text-sm md:text-base opacity-90">
              {getLocalizedValue(stat.label, locale, "")}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
)

type HomePageContentProps = {
  pageData: PageHome | null
  featuredProducts: Product[]
  latestResources: Resource[]
  locale: Locale
}

const HomePageContent: FC<HomePageContentProps> = ({
  pageData,
  featuredProducts,
  latestResources,
  locale,
}) => {
  const heroTitle = getHeroTitle(pageData, locale)
  const heroSubtitle = getHeroSubtitle(pageData, locale)
  const heroCta = getHeroCta(pageData, locale)

  // Pre-compute visibility flags to reduce complexity
  const hasStatistics =
    Boolean(pageData?.statistics) && (pageData?.statistics?.length ?? 0) > 0
  const hasFeatures =
    Boolean(pageData?.features) && (pageData?.features?.length ?? 0) > 0
  const hasCompanySection = Boolean(pageData?.companySection)
  const hasFeaturedProducts = featuredProducts.length > 0
  const hasLatestResources = latestResources.length > 0
  const hasCtaSection = Boolean(pageData?.ctaSection)

  return (
    <>
      <Hero
        title={heroTitle}
        subtitle={heroSubtitle}
        ctaText={heroCta}
        onCtaClick={scrollToFeatures}
        videoSrc={pageData?.heroVideo?.asset?.url ?? "/assets/hero.mp4"}
        posterSrc={pageData?.heroPoster?.asset?.url ?? "/assets/poster.jpg"}
      />

      {/* Statistics Section */}
      {hasStatistics && pageData?.statistics && (
        <StatisticsSection statistics={pageData.statistics} locale={locale} />
      )}

      {/* Features Section */}
      {hasFeatures && pageData?.features ? (
        <section
          id="features"
          className="py-24 bg-linear-to-b from-white to-gray-50"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <Badge
                variant="outline"
                className="mb-4 text-[#0077B6] border-[#0077B6]"
              >
                {locale === "tr" ? "Neden Poremat?" : "Why Poremat?"}
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="gradient-brand">
                  {locale === "tr"
                    ? "Yenilikçi Özellikler"
                    : "Innovative Features"}
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {locale === "tr"
                  ? "Temiz enerji ve çevre teknolojileri için yenilikçi gözenekli malzemeler geliştiriyoruz."
                  : "Developing innovative porous materials for clean energy and environmental technologies."}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {pageData.features.slice(0, 6).map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all duration-300 border-t-4 border-t-[#0077B6] group">
                    <CardHeader>
                      <div className="mb-4 w-12 h-12 bg-linear-to-br from-[#0077B6] via-[#1FC1B1] to-[#568F22] rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                        {renderFeatureIcon()}
                      </div>
                      <CardTitle className="text-xl">
                        {getLocalizedValue(feature.title, locale, "")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base mb-4">
                        {getLocalizedValue(feature.description, locale, "")}
                      </CardDescription>
                      {typeof feature.slug === "string" &&
                        feature.slug.length > 0 && (
                          <Link
                            to={`/our-approach#${feature.slug}`}
                            className="inline-flex items-center text-[#0077B6] hover:text-[#568F22] font-medium group/link"
                          >
                            <span>
                              {locale === "tr" ? "Daha Fazla" : "Learn More"}
                            </span>
                            <FontAwesomeIcon
                              icon={faArrowRight}
                              className="w-4 h-4 ml-1 group-hover/link:translate-x-1 transition-transform"
                            />
                          </Link>
                        )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <Button asChild size="lg" variant="outline">
                <Link
                  to="/our-approach"
                  className="inline-flex items-center gap-2"
                >
                  <span>
                    {locale === "tr"
                      ? "Tüm Özellikleri Keşfedin"
                      : "Explore All Features"}
                  </span>
                  <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      ) : (
        // Fallback Features Section
        <section className="py-24 bg-linear-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <Badge
                variant="outline"
                className="mb-4 text-[#0077B6] border-[#0077B6]"
              >
                {locale === "tr" ? "Neden Poremat?" : "Why Poremat?"}
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="gradient-brand">
                  {locale === "tr"
                    ? "Yenilikçi MOF Çözümleri"
                    : "Innovative MOF Solutions"}
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {locale === "tr"
                  ? "Malzeme biliminde lider, hidrojen depolama ve temiz enerji çözümlerinde öncü."
                  : "Leading in materials science, pioneering hydrogen storage and clean energy solutions."}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  title:
                    locale === "tr" ? "Yüksek Performans" : "High Performance",
                  description:
                    locale === "tr"
                      ? "Endüstri lideri depolama kapasitesi ve verimlilik"
                      : "Industry-leading storage capacity and efficiency",
                },
                {
                  title: locale === "tr" ? "Ölçeklenebilir" : "Scalable",
                  description:
                    locale === "tr"
                      ? "Laboratuvardan üretime sorunsuz geçiş"
                      : "Seamless transition from lab to production",
                },
                {
                  title: locale === "tr" ? "Uzman Ekip" : "Expert Team",
                  description:
                    locale === "tr"
                      ? "Dünya çapında tanınan bilim insanları"
                      : "World-renowned scientists and engineers",
                },
                {
                  title: locale === "tr" ? "Modüler Tasarım" : "Modular Design",
                  description:
                    locale === "tr"
                      ? "Esnek ve özelleştirilebilir çözümler"
                      : "Flexible and customizable solutions",
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-2 hover:border-[#0077B6]/20">
                    <CardHeader>
                      <div className="mb-4">
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="w-10 h-10 text-[#0077B6]"
                        />
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">
                        {feature.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Company/About Us Section */}
      {hasCompanySection && pageData?.companySection && (
        <section className="py-24 bg-linear-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                {pageData.companySection.image ? (
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={urlForImage(pageData.companySection.image)
                        .width(800)
                        .height(600)
                        .fit("crop")
                        .url()}
                      alt={
                        typeof pageData.companySection.image.alt === "string"
                          ? pageData.companySection.image.alt
                          : getLocalizedValue(
                              pageData.companySection.title,
                              locale,
                              ""
                            )
                      }
                      className="w-full h-full object-cover"
                    />
                    {/* Decorative overlay */}
                    <div className="absolute inset-0 bg-linear-to-tr from-[#0077B6]/20 to-transparent" />
                  </div>
                ) : (
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-linear-to-br from-[#0077B6] via-[#1FC1B1] to-[#568F22] aspect-4/3 flex items-center justify-center">
                    <div className="text-white text-6xl font-bold opacity-20">
                      POREMAT
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Badge
                  variant="outline"
                  className="mb-4 text-[#0077B6] border-[#0077B6]"
                >
                  {locale === "tr" ? "Hakkımızda" : "About Us"}
                </Badge>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  <span className="gradient-brand">
                    {getLocalizedValue(
                      pageData.companySection.title,
                      locale,
                      ""
                    )}
                  </span>
                </h2>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  {getLocalizedValue(
                    pageData.companySection.description,
                    locale,
                    ""
                  )}
                </p>
                <Button asChild size="lg" className="group">
                  <Link
                    to="/company"
                    className="inline-flex items-center gap-2"
                  >
                    <span>
                      {getLocalizedValue(
                        pageData.companySection.buttonText,
                        locale,
                        locale === "tr" ? "Daha Fazla Bilgi" : "Learn More"
                      )}
                    </span>
                    <FontAwesomeIcon
                      icon={faArrowRight}
                      className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                    />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Featured Products Section */}
      {hasFeaturedProducts && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <Badge
                variant="outline"
                className="mb-4 text-[#0077B6] border-[#0077B6]"
              >
                {locale === "tr" ? "Ürünlerimiz" : "Our Products"}
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="gradient-brand">
                  {locale === "tr"
                    ? "Öne Çıkan MOF Çözümleri"
                    : "Featured MOF Solutions"}
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {locale === "tr"
                  ? "Farklı uygulama ihtiyaçlarına yönelik tasarlanan MOF tabanlı malzeme platformlarımızı keşfedin."
                  : "Explore our MOF-based material platforms designed for diverse application needs."}
              </p>
            </motion.div>

            <HorizontalSlider
              className="mb-12"
              labels={
                locale === "tr"
                  ? { previous: "Önceki ürünler", next: "Sonraki ürünler" }
                  : { previous: "Previous products", next: "Next products" }
              }
            >
              {featuredProducts.map((product, index) => (
                <motion.div
                  key={product._id}
                  className="h-full"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: Math.min(index, 3) * 0.1 }}
                >
                  <Link
                    to={`/products/${product.slug.current}`}
                    className="block h-full"
                  >
                    <Card className="h-full hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#0077B6]/30 group overflow-hidden">
                      <div className="relative aspect-video overflow-hidden bg-linear-to-br from-[#0077B6]/10 via-[#1FC1B1]/10 to-[#568F22]/10">
                        {getProductImages(product).length > 0 ? (
                          <ProductImageCarousel
                            images={getProductImages(product)}
                            alt={getLocalizedValue(product.title, locale, "")}
                            width={600}
                            height={400}
                            interval={
                              PRODUCT_CAROUSEL_INTERVALS[
                                index % PRODUCT_CAROUSEL_INTERVALS.length
                              ]
                            }
                            showArrows={false}
                            className="w-full h-full"
                            imageClassName="transition-[opacity,transform] group-hover:scale-110"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-6xl gradient-brand font-bold opacity-20">
                              {getLocalizedValue(
                                product.title,
                                locale,
                                ""
                              ).charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <CardHeader>
                        <CardTitle className="text-xl group-hover:text-[#0077B6] transition-colors">
                          {getLocalizedValue(product.title, locale, "")}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-base line-clamp-3 mb-4">
                          {getLocalizedValue(product.excerpt, locale, "")}
                        </CardDescription>
                        <div className="flex items-center text-[#0077B6] font-medium group-hover:text-[#568F22] transition-colors">
                          <span>
                            {locale === "tr" ? "Detaylar" : "Details"}
                          </span>
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </HorizontalSlider>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <Button asChild size="lg" variant="outline">
                <Link to="/products" className="inline-flex items-center gap-2">
                  <span>
                    {locale === "tr"
                      ? "Tüm Ürünleri Görüntüle"
                      : "View All Products"}
                  </span>
                  <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      {/* Latest Resources Section */}
      {hasLatestResources && (
        <section className="py-24 bg-linear-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <Badge
                variant="outline"
                className="mb-4 text-[#568F22] border-[#568F22]"
              >
                {locale === "tr" ? "Kaynaklar" : "Resources"}
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="gradient-brand">
                  {locale === "tr"
                    ? "Son Haberler & Yayınlar"
                    : "Latest News & Publications"}
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {locale === "tr"
                  ? "Araştırmalarımız, gelişmelerimiz ve sektör içgörülerimizden haberdar olun"
                  : "Stay updated with our research, developments, and industry insights"}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {latestResources.map((resource, index) => (
                <motion.div
                  key={resource._id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link to={`/resources/${resource.slug.current}`}>
                    <Card className="h-full hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#568F22]/30 group">
                      <CardHeader>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                          <FontAwesomeIcon
                            icon={faCalendar}
                            className="w-4 h-4"
                          />
                          <time>
                            {new Date(resource.publishedAt).toLocaleDateString(
                              locale === "tr" ? "tr-TR" : "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            )}
                          </time>
                        </div>
                        <CardTitle className="text-xl group-hover:text-[#568F22] transition-colors line-clamp-2">
                          {getLocalizedValue(resource.title, locale, "")}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-base line-clamp-3 mb-4">
                          {getLocalizedValue(resource.excerpt, locale, "")}
                        </CardDescription>
                        <div className="flex items-center text-[#568F22] font-medium group-hover:text-[#0077B6] transition-colors">
                          <span>
                            {locale === "tr" ? "Devamını Oku" : "Read More"}
                          </span>
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <Button asChild size="lg" variant="outline">
                <Link
                  to="/resources"
                  className="inline-flex items-center gap-2"
                >
                  <span>
                    {locale === "tr"
                      ? "Tüm Kaynakları Görüntüle"
                      : "View All Resources"}
                  </span>
                  <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      {pageData?.careerSection && (
        <CareerSection section={pageData.careerSection} locale={locale} />
      )}

      {/* CTA Section */}
      {hasCtaSection && pageData?.ctaSection ? (
        <section className="relative py-24 bg-linear-to-br from-[#0077B6] via-[#1FC1B1] to-[#568F22] text-white overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                {getLocalizedValue(pageData.ctaSection.title, locale, "")}
              </h2>
              <p className="text-xl mb-10 max-w-3xl mx-auto opacity-90">
                {getLocalizedValue(pageData.ctaSection.description, locale, "")}
              </p>
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="text-lg hover:scale-105 transition-transform"
              >
                <Link to={pageData.ctaSection.primaryButtonLink}>
                  {getLocalizedValue(
                    pageData.ctaSection.primaryButton,
                    locale,
                    ""
                  )}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="w-5 h-5 ml-2"
                  />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      ) : (
        <section className="py-24 bg-linear-to-br from-[#0077B6] via-[#1FC1B1] to-[#568F22] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                {locale === "tr"
                  ? "Geleceği Birlikte İnşa Edelim"
                  : "Let's Build the Future Together"}
              </h2>
              <p className="text-xl mb-10 max-w-3xl mx-auto opacity-90">
                {locale === "tr"
                  ? "Temiz enerji çözümlerinde ortağınız olmaya hazırız. Projeleriniz için bizimle iletişime geçin."
                  : "Ready to be your partner in clean energy solutions. Get in touch for your projects."}
              </p>
              <Button asChild size="lg" variant="secondary" className="text-lg">
                <Link to="/contact">
                  {locale === "tr" ? "İletişime Geçin" : "Contact Us"}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="w-5 h-5 ml-2"
                  />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      )}
    </>
  )
}

const Component: FC = () => {
  const { locale } = useI18n()
  const [pageData, setPageData] = useState<PageHome | null>(null)
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([])
  const [latestResources, setLatestResources] = useState<Resource[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const homeQuery = `*[_type == "pageHome"][0]{
          ${pageHomeFragment}
        }`
        const productsQuery = `*[_type == "product"] | order(coalesce(order, 999) asc, _createdAt desc) {
          ${productsListFragment}
        }`
        const resourcesQuery = `*[_type == "resource"] | order(publishedAt desc) [0...3] {
          ${resourcesListFragment}
        }`

        const [homeData, productsData, resourcesData] = await Promise.all([
          sanityClient.fetch<PageHome>(homeQuery),
          sanityClient.fetch<Product[]>(productsQuery),
          sanityClient.fetch<Resource[]>(resourcesQuery),
        ])

        setPageData(homeData)
        setFeaturedProducts(productsData ?? [])
        setLatestResources(resourcesData ?? [])
      } catch (error) {
        console.error("Error fetching home page data:", error)
      } finally {
        setLoading(false)
      }
    }

    void fetchData()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0077B6] border-t-transparent" />
      </div>
    )
  }

  return (
    <HomePageContent
      pageData={pageData}
      featuredProducts={featuredProducts}
      latestResources={latestResources}
      locale={locale}
    />
  )
}

export default Component
